import React, { useState } from 'react';
import { Loader2, AlertCircle, RefreshCw, PackageX, PackageSearch, Tag, CalendarX, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';
import Pagination from './Pagination';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

interface MissingRow {
    bbrCode: string;
    vintage: string | null;
    description: string | null;
    priceExTax: string | null;
    pageUrl: string | null;
    lwin: number | null;
}

interface DbRow {
    externalVintageId: string;
    name: string | null;
    producer: string | null;
    vintage: number | null;
}

interface EventRow {
    masterProductId: string;
    name: string | null;
    sourceName: string | null;
}

interface CoverageReport {
    csvRows: number;
    csvDistinctCodes: number;
    dbBbxVintages: number;
    missingFromDbCount: number;
    missingFromDbSample: MissingRow[];
    vanishedFromBbxCount: number;
    vanishedFromBbxSample: DbRow[];
    lwinGapCount: number;
    lwinGapSample: DbRow[];
    eventLikeCount: number;
    eventLikeSample: EventRow[];
}

type BucketKey = 'missing' | 'vanished' | 'lwin' | 'event';

const BbxCoverage: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [report, setReport] = useState<CoverageReport | null>(null);
    const [ranAt, setRanAt] = useState<string | null>(null);
    const [openBucket, setOpenBucket] = useState<BucketKey | null>(null);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);

    // Reset to the first page whenever the open bucket changes.
    const selectBucket = (key: BucketKey) => {
        setOpenBucket(prev => (prev === key ? null : key));
        setPage(0);
    };

    const runCheck = async () => {
        try {
            setLoading(true);
            setError(null);
            const resp = await fetch(`${API_BASE_URL}/api/coverage/bbx`);
            if (!resp.ok) {
                throw new Error(`HTTP ${resp.status}`);
            }
            const data: CoverageReport = await resp.json();
            setReport(data);
            setRanAt(new Date().toLocaleString('en-GB'));
            setOpenBucket(null);
            setPage(0);
        } catch (err) {
            setError('Failed to run coverage check. Is the backend running with the latest build?');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const statCardStyle = (active: boolean, accent: string): React.CSSProperties => ({
        padding: '16px',
        backgroundColor: active ? '#f0f9ff' : '#f9fafb',
        borderRadius: '8px',
        border: `1px solid ${active ? accent : '#e5e7eb'}`,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
    });
    const statValueStyle = (accent: string): React.CSSProperties => ({
        fontSize: '28px',
        fontWeight: 700,
        color: accent,
    });

    const styles: { [k: string]: React.CSSProperties } = {
        container: {
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px',
        },
        title: {
            fontSize: '20px',
            fontWeight: 600,
            color: '#1f2937',
            margin: 0,
        },
        subtitle: {
            fontSize: '13px',
            color: '#6b7280',
            margin: '0 0 16px 0',
        },
        button: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500,
        },
        grid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '12px',
            marginTop: '16px',
        },
        statTop: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px',
        },
        statLabel: {
            fontSize: '12px',
            color: '#6b7280',
            fontWeight: 500,
            textTransform: 'uppercase',
        },
        statHint: {
            fontSize: '12px',
            color: '#9ca3af',
            marginTop: '4px',
        },
        totals: {
            fontSize: '13px',
            color: '#6b7280',
            marginTop: '16px',
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '12px',
            marginTop: '16px',
        },
        th: {
            textAlign: 'left',
            padding: '8px 12px',
            fontWeight: 600,
            color: '#374151',
            borderBottom: '2px solid #e5e7eb',
            backgroundColor: '#f9fafb',
        },
        td: {
            padding: '8px 12px',
            borderBottom: '1px solid #f3f4f6',
            verticalAlign: 'middle',
        },
        link: {
            color: '#3b82f6',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
        },
        errorBox: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px',
            marginTop: '16px',
            color: '#991b1b',
            fontSize: '13px',
        },
        sampleNote: {
            fontSize: '12px',
            color: '#9ca3af',
            margin: '12px 0 0 0',
        },
    };

    const buckets: { key: BucketKey; label: string; count: number; accent: string; icon: React.ReactNode; hint: string }[] = report ? [
        { key: 'missing', label: 'Missing from DB', count: report.missingFromDbCount, accent: '#dc2626', icon: <PackageSearch size={16} color="#dc2626" />, hint: 'BBX lists these, we never imported' },
        { key: 'vanished', label: 'Vanished from BBX', count: report.vanishedFromBbxCount, accent: '#f59e0b', icon: <PackageX size={16} color="#f59e0b" />, hint: 'In our DB, gone from BBX (sold out)' },
        { key: 'lwin', label: 'LWIN gaps', count: report.lwinGapCount, accent: '#7c3aed', icon: <Tag size={16} color="#7c3aed" />, hint: 'CSV has a LWIN, ours is blank' },
        { key: 'event', label: 'Event-like leaks', count: report.eventLikeCount, accent: report.eventLikeCount > 0 ? '#dc2626' : '#10b981', icon: <CalendarX size={16} color={report.eventLikeCount > 0 ? '#dc2626' : '#10b981'} />, hint: 'Non-wine items in the catalog' },
    ] : [];

    const renderSample = () => {
        if (!report || !openBucket) return null;

        const allRows: any[] =
            openBucket === 'missing' ? report.missingFromDbSample :
            openBucket === 'vanished' ? report.vanishedFromBbxSample :
            openBucket === 'lwin' ? report.lwinGapSample :
            report.eventLikeSample;

        if (allRows.length === 0) {
            return openBucket === 'event'
                ? <p style={styles.sampleNote}>No event-like products found — catalog is clean. ✓</p>
                : <p style={styles.sampleNote}>Nothing to show.</p>;
        }

        const totalPages = Math.max(1, Math.ceil(allRows.length / pageSize));
        const safePage = Math.min(page, totalPages - 1);
        const start = safePage * pageSize;
        const pageRows = allRows.slice(start, start + pageSize);

        const table = openBucket === 'missing' ? (
            <table style={styles.table}>
                <thead><tr>
                    <th style={styles.th}>BBR Code</th>
                    <th style={styles.th}>Vintage</th>
                    <th style={styles.th}>Description</th>
                    <th style={styles.th}>Price (ex-tax)</th>
                    <th style={styles.th}>LWIN?</th>
                    <th style={styles.th}>Link</th>
                </tr></thead>
                <tbody>
                    {(pageRows as MissingRow[]).map((r, i) => (
                        <tr key={i}>
                            <td style={styles.td}>{r.bbrCode}</td>
                            <td style={styles.td}>{r.vintage || <span style={{ color: '#9ca3af' }}>NV</span>}</td>
                            <td style={styles.td}>{r.description}</td>
                            <td style={styles.td}>{r.priceExTax}</td>
                            <td style={styles.td}>{r.lwin ? '✓' : '—'}</td>
                            <td style={styles.td}>
                                {r.pageUrl && <a style={styles.link} href={r.pageUrl} target="_blank" rel="noreferrer">view <ExternalLink size={11} /></a>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        ) : openBucket === 'event' ? (
            <table style={styles.table}>
                <thead><tr>
                    <th style={styles.th}>Product ID</th>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Source name</th>
                </tr></thead>
                <tbody>
                    {(pageRows as EventRow[]).map((r, i) => (
                        <tr key={i}>
                            <td style={styles.td}>{r.masterProductId}</td>
                            <td style={styles.td}>{r.name}</td>
                            <td style={styles.td}>{r.sourceName}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        ) : (
            <table style={styles.table}>
                <thead><tr>
                    <th style={styles.th}>Vintage ID</th>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Producer</th>
                    <th style={styles.th}>Vintage</th>
                </tr></thead>
                <tbody>
                    {(pageRows as DbRow[]).map((r, i) => (
                        <tr key={i}>
                            <td style={styles.td}>{r.externalVintageId}</td>
                            <td style={styles.td}>{r.name}</td>
                            <td style={styles.td}>{r.producer}</td>
                            <td style={styles.td}>{r.vintage}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );

        return (
            <>
                {table}
                <p style={styles.sampleNote}>
                    Showing {start + 1}–{start + pageRows.length} of {allRows.length.toLocaleString()}.
                </p>
                {totalPages > 1 && (
                    <Pagination
                        page={safePage}
                        totalPages={totalPages}
                        size={pageSize}
                        onPageChange={setPage}
                        onSizeChange={(s) => { setPageSize(s); setPage(0); }}
                    />
                )}
            </>
        );
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h2 style={styles.title}>BBX Coverage Check</h2>
                <button style={styles.button} onClick={runCheck} disabled={loading}>
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                    {loading ? 'Checking…' : 'Run coverage check'}
                </button>
            </div>
            <p style={styles.subtitle}>
                Diffs Berry Bros' hourly bulk CSV against our database — read-only, writes nothing.
                {ranAt && <span> Last run: {ranAt}.</span>}
            </p>

            {error && (
                <div style={styles.errorBox}>
                    <AlertCircle size={16} />
                    {error}
                </div>
            )}

            {report && (
                <>
                    <div style={styles.grid}>
                        {buckets.map(b => {
                            const active = openBucket === b.key;
                            return (
                                <div
                                    key={b.key}
                                    style={statCardStyle(active, b.accent)}
                                    onClick={() => selectBucket(b.key)}
                                >
                                    <div style={styles.statTop}>
                                        {b.icon}
                                        <span style={styles.statLabel}>{b.label}</span>
                                        {active ? <ChevronDown size={14} color="#9ca3af" /> : <ChevronRight size={14} color="#9ca3af" />}
                                    </div>
                                    <div style={statValueStyle(b.accent)}>{b.count.toLocaleString()}</div>
                                    <div style={styles.statHint}>{b.hint}</div>
                                </div>
                            );
                        })}
                    </div>

                    <div style={styles.totals}>
                        CSV: {report.csvDistinctCodes.toLocaleString()} distinct listings ({report.csvRows.toLocaleString()} rows)
                        &nbsp;·&nbsp; Our BBX vintages: {report.dbBbxVintages.toLocaleString()}
                    </div>

                    {renderSample()}
                </>
            )}
        </div>
    );
};

export default BbxCoverage;
